/**
 * Cloudflare Calls client — speaks to our worker's `/calls/*` proxy. The
 * worker forwards with the Calls app secret; the client only ever holds
 * its user session token.
 */
import { ApiError, type ChatClient } from './api.js';

export interface CallsSession {
  readonly sessionId: string;
  readonly mode: 'real' | 'stub';
}

export interface TrackInput {
  readonly location: 'local' | 'remote';
  readonly mid?: string;
  readonly trackName?: string;
  /** For remote pulls: the Calls session that owns the track. */
  readonly sessionId?: string;
}

export interface TracksNewRequest {
  readonly sessionDescription?: RTCSessionDescriptionInit;
  readonly tracks: readonly TrackInput[];
}

export interface TracksNewResponse {
  readonly sessionDescription?: RTCSessionDescriptionInit;
  readonly tracks: readonly { readonly mid: string; readonly trackName: string }[];
  readonly mode?: 'real' | 'stub';
}

export class CallsClient {
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #tokenProvider: () => Promise<string> | string;

  constructor(deps: {
    baseUrl: string;
    tokenProvider: () => Promise<string> | string;
    fetchImpl?: typeof fetch;
  }) {
    this.#baseUrl = deps.baseUrl.replace(/\/$/, '');
    this.#tokenProvider = deps.tokenProvider;
    this.#fetch = deps.fetchImpl ?? ((...args) => globalThis.fetch(...args));
  }

  static fromChatClient(client: ChatClient, tokenProvider: () => Promise<string> | string) {
    // ChatClient exposes its base URL privately, but we can reuse it by
    // routing through the same relative paths. Keep them as '' to match the
    // same-origin convention used everywhere else in the web client.
    return new CallsClient({
      baseUrl: '',
      tokenProvider,
      fetchImpl: (input, init) => client.rawFetch(input, init),
    });
  }

  async createSession(): Promise<CallsSession> {
    return this.#request<CallsSession>('POST', '/calls/sessions');
  }

  async newTracks(sessionId: string, body: TracksNewRequest): Promise<TracksNewResponse> {
    return this.#request<TracksNewResponse>(
      'POST',
      `/calls/sessions/${encodeURIComponent(sessionId)}/tracks/new`,
      body,
    );
  }

  async renegotiate(
    sessionId: string,
    sessionDescription: RTCSessionDescriptionInit,
  ): Promise<unknown> {
    return this.#request('PUT', `/calls/sessions/${encodeURIComponent(sessionId)}/renegotiate`, {
      sessionDescription,
    });
  }

  async closeTracks(
    sessionId: string,
    tracks: readonly { readonly mid: string }[],
  ): Promise<unknown> {
    return this.#request('PUT', `/calls/sessions/${encodeURIComponent(sessionId)}/tracks/close`, {
      tracks,
    });
  }

  async #request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await this.#tokenProvider();
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    if (body !== undefined) headers['content-type'] = 'application/json';
    const res = await this.#fetch(`${this.#baseUrl}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const text = await res.text();
    if (!res.ok) {
      let message = text || res.statusText;
      try {
        const parsed = JSON.parse(text) as { error?: string };
        if (parsed.error) message = parsed.error;
      } catch {
        // keep plain text
      }
      throw new ApiError(res.status, message);
    }
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }
}
